import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Plus, Trash2, Save, Edit2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","PR"];

export default function AddressesPage() {
  const [addrs, setAddrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ line_1: '', line_2: '', city: '', state: '', postal_code: '', country: 'US' });
  const [editId, setEditId] = useState(null);

  useEffect(() => { loadAddrs(); }, []);
  const loadAddrs = () => { api.get('/account/addresses').then(r => setAddrs(r.data)).catch(() => {}).finally(() => setLoading(false)); };

  const handleSave = async () => {
    if (!form.line_1 || !form.city || !form.state || !form.postal_code) { toast.error('Fill required fields'); return; }
    try {
      if (editId) { await api.put(`/account/addresses/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/account/addresses', form); toast.success('Added'); }
      setShowNew(false); setEditId(null); setForm({ line_1: '', line_2: '', city: '', state: '', postal_code: '', country: 'US' }); loadAddrs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (a) => { setEditId(a.id); setForm({ line_1: a.line_1, line_2: a.line_2 || '', city: a.city, state: a.state, postal_code: a.postal_code, country: a.country || 'US' }); setShowNew(true); };
  const handleDelete = async (id) => { await api.delete(`/account/addresses/${id}`); toast.success('Deleted'); loadAddrs(); };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="addresses-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-medium text-ink mb-1">My Addresses</h1><p className="text-sm text-muted-foreground">Manage your shipping addresses.</p></div>
        <Button size="sm" className="gap-1.5 rounded-[12px]" onClick={() => { setShowNew(true); setEditId(null); setForm({ line_1: '', line_2: '', city: '', state: '', postal_code: '', country: 'US' }); }}><Plus className="h-3.5 w-3.5" /> Add Address</Button>
      </div>

      {showNew && (
        <Card className="bg-white border-stone/50 mb-4"><CardContent className="p-4">
          <h3 className="text-sm font-semibold text-ink mb-3">{editId ? 'Edit Address' : 'New Address'}</h3>
          <div className="space-y-3">
            <div><Label className="text-xs">Street Address</Label><Input value={form.line_1} onChange={e => setForm({...form, line_1: e.target.value})} className="mt-1" /></div>
            <div><Label className="text-xs">Apt / Suite</Label><Input value={form.line_2} onChange={e => setForm({...form, line_2: e.target.value})} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs">State</Label><Select value={form.state} onValueChange={v => setForm({...form, state: v})}><SelectTrigger className="mt-1"><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">ZIP</Label><Input value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex gap-2"><Button size="sm" className="gap-1" onClick={handleSave}><Save className="h-3 w-3" /> Save</Button><Button size="sm" variant="outline" onClick={() => { setShowNew(false); setEditId(null); }}>Cancel</Button></div>
          </div>
        </CardContent></Card>
      )}

      {addrs.length === 0 && !showNew ? (
        <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><MapPin className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No saved addresses.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {addrs.map(a => (
            <Card key={a.id} className="bg-white border-stone/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div><p className="text-sm text-ink">{a.line_1}{a.line_2 ? `, ${a.line_2}` : ''}</p><p className="text-xs text-muted-foreground">{a.city}, {a.state} {a.postal_code}</p></div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(a)}><Edit2 className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(a.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
